import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { Tree, TreeDataNode, TreeProps } from "antd-latest";
import { Checkbox, Icon, Input } from "antd";
import { FaFolderTree } from "react-icons/fa6";
import styles from "../../styles.module.scss";
import { Flex } from "@components/ui/Flex";
import { DataSourceHandler } from "@api/data-source-handler";
import { RootState } from "@store/types";
import { errorHandlerFor400 } from "@api/services/errorhandler";
import { Text } from "@components/ui/Text";
import { ShowWhenTrue } from "@helpers/showwhentrue";
import { FaFolder } from "react-icons/fa";
import { _canvasdagFormInitialValues } from "@pages/workflow-page/utils";
import { _selectoptionType } from "@components/form/select-field";
import { ModelRequestType, RootSearchType, RootType } from "./constant";
import HierarchyView from "./view";
import { TableMetaData } from "@pages/data-browser/treeviews/connections-treeview";
import { InPageSpinner } from "@components/spinners/in-page-spinner";
import { Attribute } from "@pages/admin/asset_heirarchy/types";
import { errorAlert, successAlert } from "@components/toastify/notify-toast";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { TooltipTop } from "@components/tooltips";

const { Search } = Input;

type Props = {
    activeRecordsMetadata: TableMetaData;
    formValuesForEdit: ModelRequestType | null;
    setFormValuesForEdit: (values: ModelRequestType | null) => void;
    handleGoToReqPage: () => void;
    isRDBMSView?: boolean;
    isAspenView?: boolean;
}

const Hierarchy: React.FC<Props> = ({ activeRecordsMetadata, handleGoToReqPage, formValuesForEdit, setFormValuesForEdit, isRDBMSView = false, isAspenView = false }) => {
    const [isHandleSelectRunning, setIsHandleSelectRunning] = useState(false);
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [selectedElements, setSelectedElements] = useState<RootType>({} as RootType);
    const [isLoading, setIsLoading] = useState(true);
    const [tagPosition, setTagPosition] = useState("Elements");
    const [isGlobalSearch, setIsGlobalSearch] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [isLoadingElemets, setIsLoadingElements] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [searchTxt, setSearchTxt] = useState("");
    const [filteredData, setFilteredData] = useState<RootType[]>([]);
    const [filteredPipointData, setPipointFilteredData] = useState<RootType[]>([]);
    const [roots, setRoots] = useState<RootType[]>([]);
    const [searchRoots, setSearchRoots] = useState<RootSearchType[]>([]);
    const [pipoints, setPipoints] = useState<Attribute[]>([]);
    const [activeStep, setActiveStep] = useState(0);

    const handleRowSelection = (record: any) => {
        const rowKey = record.key;
        const selectedIndex = selectedRowKeys.indexOf(rowKey);
        const newSelectedKeys = [...selectedRowKeys];

        if (selectedIndex > -1) {
            newSelectedKeys.splice(selectedIndex, 1);
        } else {
            newSelectedKeys.push(rowKey);
        }

        setSelectedRowKeys(newSelectedKeys);
    };

    const handleSearch = (confirm: () => void) => {
        confirm();
    };
    const handleReset = (clearFilters: () => void, setSelectedKeys: (keys: any[]) => void) => {
        setSelectedKeys([]);
        clearFilters();
    };

    const getColumnSearchProps = (dataIndex: string) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
                <input
                    className="search_input"
                    placeholder={`Search ${dataIndex}`}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch(confirm)}
                    value={selectedKeys[0] || ''}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Flex gap="10px">
                    <button className="btn btn-yellow btn-sm" style={{ width: 90 }} onClick={() => handleSearch(confirm)}>Search</button>
                    <button className="btn btn-grey btn-sm" style={{ width: 90 }} onClick={() => handleReset(clearFilters, setSelectedKeys)}>Reset</button>
                </Flex>
            </div>
        ),
        onFilter: (value: any, record: any) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
        filterIcon: (filtered: any) => (
            <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        render: (text: any) => text,
    });

    const mapToTreeData = (
        items: (RootType | RootSearchType)[],
        seenIds = new Set<string>()
    ): TreeDataNode[] => {
        return items.reduce<TreeDataNode[]>((acc, item) => {
            if (seenIds.has(item.element_id)) {
                return acc;
            }

            seenIds.add(item.element_id);

            const node: TreeDataNode = {
                title: item.element_name,
                key: item.element_id,
                children: item.children ? mapToTreeData(item.children, seenIds) : [],
            };

            acc.push(node);
            return acc;
        }, []);
    };

    const mapToRDBMSTreeData = (
        items: (RootType | RootSearchType)[],
        seenIds = new Set<string>()
    ): TreeDataNode[] => {
        return items.reduce<TreeDataNode[]>((acc, item) => {
            if (seenIds.has(item.elementId)) {
                return acc;
            }

            seenIds.add(item.elementId);

            const node: TreeDataNode = {
                title: item?.originalName,
                key: item?.elementId,
                children: item?.columns ? mapToRDBMSTreeData(item.columns, seenIds) : [],
            };

            acc.push(node);
            return acc;
        }, []);
    };

    const getRoots = () => {
        setIsLoading(true)
        DataSourceHandler.GetRoots(
            `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, activeRecordsMetadata?.managedNodeInfo?.nodeName || "",
            activeRecordsMetadata?.afStructureDto?.serverName || "",
            activeRecordsMetadata?.afStructureDto?.databaseName || "",
            (response) => {
                setRoots(response)
                setIsLoading(false)
            },
            (error: any) => {
                errorHandlerFor400(error)
                setIsLoading(false)
            },
        );
    };

    const getRDBMSRoots = () => {
        setIsLoading(true)
        DataSourceHandler.GetRDBMSRoots(
            `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, activeRecordsMetadata?.managedNodeInfo?.nodeName || "",
            activeRecordsMetadata?.afStructureDto?.serverName || "",
            activeRecordsMetadata?.afStructureDto?.databaseName || "",
            (response) => {
                setRoots(response?.tables)
                setIsLoading(false)
            },
            (error: any) => {
                errorHandlerFor400(error)
                setIsLoading(false)
            },
        );
    };

    const getPipoints = () => {
        setIsLoading(true)
        DataSourceHandler.GetPipoints(
            `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, activeRecordsMetadata?.managedNodeInfo?.nodeName || "",
            activeRecordsMetadata?.afStructureDto?.serverName || "",
            activeRecordsMetadata?.afStructureDto?.databaseName || "",
            (response) => {
                const _pipoints = response.map((item: any) => ({
                    key: item.pipointName,
                    pipoint_id: item.pipoint_id,
                    pipointName: item.pipointName,
                    pipointType: item.pipointType,
                    pipointClass: item.pipointClass,
                    path: item.path,
                }));
                setPipoints(_pipoints);
                setPipointFilteredData(_pipoints || []);
                setIsLoading(false);
            },
            (error: any) => {
                errorHandlerFor400(error)
                setIsLoading(false)
            },
        );
    };

    const treeData: any = useMemo(() => isRDBMSView ? mapToRDBMSTreeData(isGlobalSearch ? searchRoots : roots) : mapToTreeData(isGlobalSearch ? searchRoots : roots), [isGlobalSearch, searchRoots, roots]);

    const handleSelect: TreeProps['onSelect'] = (selectedKeys, info) => {
        if (!info.selected) return;
        setIsHandleSelectRunning(true);

        setShowForm(false);
        setActiveStep(0);

        setIsLoadingElements(true);
        setSelectedElements({} as RootType);

        if (info.node?.key) {
            setFilteredData([]);

            DataSourceHandler.GetRootElementById(
                `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
                info.node.key?.toString() || "",
                activeRecordsMetadata?.managedNodeInfo?.nodeName || "",
                activeRecordsMetadata?.afStructureDto?.serverName || "",
                activeRecordsMetadata?.afStructureDto?.databaseName || "",
                (response) => {
                    setSelectedElements(response);

                    const attributes = response?.attributes || [];
                    setFilteredData(attributes);

                    const updateTree = (nodes: RootType[]): RootType[] => {
                        return nodes.map((node) => {
                            if (node.element_id === info.node.key?.toString()) {
                                return { ...node, children: response?.children || [] };
                            }
                            if (node.children) {
                                return { ...node, children: updateTree(node.children) };
                            }
                            return node;
                        });
                    };

                    setRoots((prevRoots) => {
                        const newRoots = updateTree(prevRoots);
                        return [...newRoots];
                    });

                    setIsLoadingElements(false);
                    setIsHandleSelectRunning(false);
                },
                (error: any) => {
                    errorHandlerFor400(error);
                    setIsLoadingElements(false);
                    setIsHandleSelectRunning(false);
                }
            );
        } else {
            setSelectedElements({} as RootType);
            setFilteredData([]);
            setIsLoadingElements(false);
        }
    };

    const tableDataSources: any = !isGlobalSearch ? useMemo(() => {
        return selectedElements?.attributes?.map((element) => ({
            key: element.attr_id,
            attr_name: element.attr_name,
            pipointName: element.pipointName,
        }));
    }, [selectedElements]) : useMemo(() => {
        return searchRoots?.map((element) => ({
            key: element.attr_id,
            attr_name: element.attr_name,
            pipointName: element.pipointName,
        }));
    }, [searchRoots]);

    const handleSelectAll = (e: CheckboxChangeEvent) => {
        const isChecked = e.target.checked;
        const allKeys = (tagPosition === "Elements"
            ? tableDataSources.map((item: any) => item.attr_name) //  matches how you generate `key`
            : pipoints.map((item: any) => item.key) // or pipoint_id if needed
        );

        setSelectedRowKeys(isChecked ? allKeys : []);
    };

    const columns: any = [
        {
            title: (
                <Checkbox
                    checked={tagPosition === "Elements" ? selectedRowKeys?.length === tableDataSources?.length && tableDataSources?.length > 0 : selectedRowKeys?.length === pipoints?.length && pipoints?.length > 0}
                    indeterminate={tagPosition === "Elements" ? selectedRowKeys?.length > 0 && selectedRowKeys?.length < tableDataSources?.length : selectedRowKeys?.length > 0 && selectedRowKeys?.length < pipoints?.length}
                    onChange={handleSelectAll}
                />

            ),
            dataIndex: 'selection',
            key: 'selection',
            width: 100,
            render: (_: any, record: any) => {
                const rowKey = record.key;
                const isChecked = selectedRowKeys.includes(rowKey);
                //     tagPosition === "Elements" ? selectedRowKeys?.length === tableDataSources?.length && tableDataSources?.length > 0 : selectedRowKeys?.length === pipoints?.length && pipoints?.length > 0,
                //     tagPosition, selectedRowKeys?.length, tableDataSources?.length
                // )
                // Create a stable component instance with a unique key
                return (
                    <Checkbox
                        checked={!!isChecked}
                        onChange={() => handleRowSelection(record)}
                    />
                );
            }
        },
        ...(tagPosition === "Elements" ? [{
            title: 'Attribute Name',
            dataIndex: 'attr_name',
            key: 'attr_name',
            sorter: (a: any, b: any) => a.attr_name?.length - b.attr_name?.length,
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('attr_name'),
        }] : [{
            title: 'PIPoint Name',
            dataIndex: 'pipointName',
            key: 'pipointName',
            sorter: (a: any, b: any) => a.pipointName?.length - b.pipointName?.length,
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('pipointName'),
        }, {
            title: 'Type',
            dataIndex: 'pipointType',
            key: 'pipointType',
            sorter: (a: any, b: any) => a.pipointType?.length - b.pipointType?.length,
            sortDirections: ['descend', 'ascend'],
        }
            // {
            //     title: 'Class',
            //     dataIndex: 'pipointClass',
            //     key: 'pipointClass',
            //     sorter: (a: any, b: any) => a.pipointClass?.length - b.pipointClass?.length,
            //     sortDirections: ['descend', 'ascend'],
            // }
            , {
            title: 'Path',
            dataIndex: 'path',
            key: 'path',
            sorter: (a: any, b: any) => a.path?.length - b.path?.length,
            sortDirections: ['descend', 'ascend'],
        }]),
    ];

    const handleTableChange = (pagination: any, filters: any, sorter: any, extra: { action: string; currentDataSource: any; }) => {
        if (tagPosition === "Elements") {
            setFilteredData(extra.currentDataSource);
        } else {
            setPipointFilteredData(extra.currentDataSource);
        }
    };

    const handleSearchAttr = (text: string) => {
        if (!isEmpty(text)) {
            setIsLoading(true)
            DataSourceHandler.GetSearchRoots(
                `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, activeRecordsMetadata?.managedNodeInfo?.nodeName || "",
                activeRecordsMetadata?.afStructureDto?.serverName || "",
                activeRecordsMetadata?.afStructureDto?.databaseName || "",
                text,
                (response) => {
                    setIsGlobalSearch(true);
                    setSearchRoots(response)
                    setIsLoading(false)
                },
                (error: any) => {
                    errorHandlerFor400(error)
                    setIsLoading(false)
                },
            );
        } else {
            setIsGlobalSearch(false);
            setSearchRoots([]);
        }

    };

    const requestJson = JSON.parse(formValuesForEdit?.requestJson || `{}`);
    const elementRootPath = requestJson?.element_root;
    const elementPathName = requestJson?.element_name;

    useEffect(() => {
        if (!isEmpty(formValuesForEdit)) {
            if (selectedElements?.path?.replace(/\\/g, "") === `${elementRootPath}${elementPathName}`.replace(/\\/g, "") && formValuesForEdit?.tagSelectedType === "Elements") {
                setSelectedRowKeys(formValuesForEdit?.tagSelections || []);
            } else if (formValuesForEdit?.tagSelectedType === "Pipoint") {
                setSelectedRowKeys(formValuesForEdit?.tagSelections || []);
            }
            else {
                setSelectedRowKeys([]);
            }
            setTagPosition(!isEmpty(activeRecordsMetadata?.afStructureDto?.serverName) && !isEmpty(activeRecordsMetadata?.afStructureDto?.databaseName) && activeRecordsMetadata?.afStructureDto?.serverType === "PI_AF" ? "Elements" : "Pipoint");
        }
        if (isEmpty(formValuesForEdit)) {
            setSelectedRowKeys([]);
            setTagPosition(!isEmpty(activeRecordsMetadata?.afStructureDto?.serverName) && !isEmpty(activeRecordsMetadata?.afStructureDto?.databaseName) && activeRecordsMetadata?.afStructureDto?.serverType === "PI_AF" ? "Elements" : "Pipoint");

        }
    }, [formValuesForEdit, selectedElements, activeRecordsMetadata]);

    useEffect(() => {
        if (!isRDBMSView && !isAspenView) {
            if (activeRecordsMetadata?.afStructureDto?.serverType === "PI_AF") {
                getRoots();
            }
            getPipoints();
        } else if (isRDBMSView) {
            getRDBMSRoots();
        } else if (isAspenView) {
            setIsLoading(false);
        }
    }, [activeRecordsMetadata]);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                successAlert('Copied To Clipboard');
            })
            .catch(() => {
                errorAlert('Failed To Copy Text');
            });
    };

    const renderIp21Title = (node: any) => (
        <div
            className={styles.treeNodeText}
            onClick={(e) => {
                e.stopPropagation();
                handleCopy(node.title);
            }}
        >
            <TooltipTop title="Click to copy" placement="top">
                <span>{node.title}</span>
            </TooltipTop>
        </div>
    );

    if (isLoading) {
        return <Flex className={styles.treeContainer} display={"flex"} flexDirection={"column"} alignItems={"center"} justifyContent={"center"}>
            <InPageSpinner size="Large" color="white" />
            <Text>Fetching data ...</Text>
        </Flex>
    }
    return (
        <div>
            <div className={styles.treeContainer}>
                <Flex gap="30px">
                    <ShowWhenTrue show={tagPosition === "Elements" || isRDBMSView}>
                        <Flex className={styles.boxHierarchyTreeView} flexDirection={"column"} gap="0px">
                            <div className={styles.labelHierarchyView}>
                                <FaFolderTree />
                                <Text fontSize={14} fontWeight={500}>Elements</Text>
                                {/* <div>
                                        <Search
                                            placeholder="Search Attribute name"
                                            allowClear
                                            value={searchTxt}
                                            onChange={(e) => setSearchTxt(e.target.value)}
                                            onSearch={handleSearchAttr}
                                            className={styles["customAttrSearch"]}
                                        />
                                    </div> */}
                            </div>
                            <ShowWhenTrue show={!isEmpty(treeData)}>
                                <Tree
                                    className="custom-tree"
                                    selectable={!isRDBMSView}
                                    showLine
                                    icon={<FaFolder />}
                                    showIcon={!isRDBMSView}
                                    onSelect={handleSelect}
                                    treeData={treeData}
                                    titleRender={isRDBMSView ? renderIp21Title : undefined}
                                />
                            </ShowWhenTrue>
                        </Flex>
                    </ShowWhenTrue>
                    <HierarchyView
                        formValuesForEdit={formValuesForEdit}
                        setFormValuesForEdit={setFormValuesForEdit}
                        selectedElements={selectedElements}
                        tableDataSources={filteredData}
                        columns={columns}
                        isRDBMSview={isRDBMSView}
                        isAspenIp21View={isAspenView}
                        isLoadingElements={isLoadingElemets}
                        setIsLoadingElements={setIsLoadingElements}
                        showForm={showForm}
                        activeStep={activeStep}
                        pipoints={pipoints}
                        activeRecordsMetadata={activeRecordsMetadata}
                        selectedRowKeys={selectedRowKeys}
                        setActiveStep={setActiveStep}
                        setShowForm={setShowForm}
                        setSelectedRowKeys={setSelectedRowKeys}
                        handleTableChange={handleTableChange}
                        handleGoToReqPage={handleGoToReqPage}
                        tagPosition={tagPosition}
                        setTagPosition={setTagPosition}
                        isHandleSelectRunning={isHandleSelectRunning}
                    />
                </Flex>
            </div>

        </div>
    );
};

export default Hierarchy;
